/**
 * Catalog Drawer Component
 *
 * Drawer to view and manage supplier catalog items with inline editing.
 * @see US-8.7 - Drawer Gestion des References
 */

import { useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Search, Package, Plus } from 'lucide-react';
import { Drawer, Input, Skeleton, EmptyState } from '@/components/ui';
import {
  useCatalogItems,
  useCatalogItem,
  useCreateCatalogItem,
  useUpdateCatalogItem,
  useUpdateCatalogItemPrice,
  useDeleteCatalogItem,
} from '../../hooks/useCatalog';
import { useActivePackagings } from '../../../packagings/hooks/usePackagings';
import { RawProductAutocomplete } from '../RawProductAutocomplete';
import { PriceHistoryModal } from '../PriceHistoryModal';
import { NewItemForm } from './NewItemForm';
import { ProductGroup } from './ProductGroup';
import { groupItemsByProduct } from './catalog.types';
import type { NewItemState } from './catalog.types';
import type { SupplierWithStats, RawProductWithRelations, SupplierCatalogItemWithDetails } from '@bstock/shared';
import { useCurrency } from '@/hooks';

interface CatalogDrawerProps {
  supplier: SupplierWithStats | null;
  isOpen: boolean;
  onClose: () => void;
}

export function CatalogDrawer({ supplier, isOpen, onClose }: CatalogDrawerProps) {
  const currency = useCurrency();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [newItem, setNewItem] = useState<NewItemState | null>(null);

  // URL-based state for PriceHistoryModal deep linking
  const { catalogItemId } = useParams<{ catalogItemId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // Check if we're on the price-history route
  const isPriceHistoryOpen = location.pathname.includes('/price-history');

  // Fetch active packagings for dropdown
  const { data: packagings = [] } = useActivePackagings();

  // Debounce search
  useMemo(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const { data, isLoading } = useCatalogItems(supplier?.id ?? null, {
    search: debouncedSearch || undefined,
    page: 1,
    limit: 100,
    sortBy: 'rawProductName',
    sortOrder: 'asc',
  });

  const items = data?.data ?? [];
  const productGroups = useMemo(() => groupItemsByProduct(items), [items]);

  // Mutations
  const createMutation = useCreateCatalogItem(supplier?.id ?? '');
  const updateMutation = useUpdateCatalogItem(supplier?.id ?? '');
  const updatePriceMutation = useUpdateCatalogItemPrice(supplier?.id ?? '');
  const deleteMutation = useDeleteCatalogItem(supplier?.id ?? '');

  const handleViewPriceHistory = useCallback((item: SupplierCatalogItemWithDetails) => {
    if (!supplier) return;
    navigate(`/parametres/suppliers/${supplier.id}/catalog/${item.id}/price-history?tab=suppliers`);
  }, [supplier, navigate]);

  const handleClosePriceHistory = useCallback(() => {
    if (!supplier) return;
    navigate(`/parametres/suppliers/${supplier.id}/catalog?tab=suppliers`);
  }, [supplier, navigate]);

  // Fetch single catalog item when coming from direct URL
  const { data: catalogItemFromUrl } = useCatalogItem(
    supplier?.id ?? null,
    isPriceHistoryOpen ? catalogItemId ?? null : null
  );

  // Find the catalog item from list first, fallback to direct fetch for deep linking
  const priceHistoryItem = catalogItemId
    ? items.find((item) => item.id === catalogItemId) ?? catalogItemFromUrl ?? null
    : null;

  const handleProductSelect = useCallback((product: RawProductWithRelations) => {
    setNewItem({
      rawProduct: product,
      packagingId: null,
      conversionToRecipeUnit: '1',
      priceExclTax: '',
      supplierRef: '',
    });
  }, []);

  const handlePackagingSelect = useCallback((packagingId: string) => {
    if (!newItem) return;
    setNewItem({
      ...newItem,
      packagingId: packagingId || null,
    });
  }, [newItem]);

  const handleCancelNewItem = useCallback(() => {
    setNewItem(null);
  }, []);

  const handleCreateItem = useCallback(async () => {
    if (!newItem || !supplier) return;

    const conversionValue = parseFloat(newItem.conversionToRecipeUnit);
    const priceValue = parseFloat(newItem.priceExclTax);

    if (!newItem.packagingId || isNaN(conversionValue) || isNaN(priceValue)) {
      return;
    }

    try {
      await createMutation.mutateAsync({
        rawProductId: newItem.rawProduct.id,
        packagingId: newItem.packagingId,
        conversionToRecipeUnit: conversionValue,
        priceExclTax: Math.round(priceValue * 100),
        supplierRef: newItem.supplierRef || null,
      });
      setNewItem(null);
    } catch {
      // Error handled by mutation
    }
  }, [newItem, supplier, createMutation]);

  if (!supplier) return null;

  // Hide drawer when price history modal is open
  const isDrawerVisible = isOpen && !isPriceHistoryOpen;

  return (
    <>
      <Drawer
        open={isDrawerVisible}
        onOpenChange={(open) => !open && onClose()}
        title={`${supplier.name} - References`}
        description="Gerez les references produits de ce fournisseur"
        size="full"
        footer={
          <div className="flex items-center justify-between text-sm text-neutral-500">
            <span>
              Total : {items.length} reference{items.length > 1 ? 's' : ''} pour{' '}
              {productGroups.length} produit{productGroups.length > 1 ? 's' : ''}
            </span>
          </div>
        }
      >
        <div className="space-y-6">
          {/* Search and Add */}
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
              <Input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Filtrer les references existantes..."
                className="pl-10"
              />
            </div>

            <div className="flex items-center gap-2">
              <Plus className="w-4 h-4 text-neutral-400 flex-shrink-0" />
              <RawProductAutocomplete
                existingCatalogItems={items}
                onSelect={handleProductSelect}
                placeholder="Ajouter un nouveau produit au catalogue..."
              />
            </div>
          </div>

          {/* New item form */}
          {newItem && (
            <NewItemForm
              newItem={newItem}
              packagings={packagings}
              currency={currency}
              onPackagingSelect={handlePackagingSelect}
              onFieldChange={(field, value) => setNewItem({ ...newItem, [field]: value })}
              onCancel={handleCancelNewItem}
              onCreate={handleCreateItem}
              isLoading={createMutation.isPending}
            />
          )}

          {/* Content */}
          {isLoading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-32" />
              ))}
            </div>
          ) : productGroups.length === 0 ? (
            <EmptyState
              icon={Package}
              title={search ? 'Aucun produit trouve' : 'Aucune reference dans le catalogue'}
              description={
                search
                  ? "Essayez avec d'autres termes de recherche"
                  : 'Utilisez la barre de recherche ci-dessus pour ajouter des produits bruts'
              }
            />
          ) : (
            <div className="space-y-6">
              {productGroups.map((group) => (
                <ProductGroup
                  key={group.rawProduct.id}
                  group={group}
                  supplierId={supplier.id}
                  packagings={packagings}
                  currency={currency}
                  editingItemId={editingItemId}
                  onEditItem={setEditingItemId}
                  onCancelEdit={() => setEditingItemId(null)}
                  onViewHistory={(itemId) => {
                    const item = items.find((i) => i.id === itemId);
                    if (item) handleViewPriceHistory(item);
                  }}
                  updateMutation={updateMutation}
                  updatePriceMutation={updatePriceMutation}
                  deleteMutation={deleteMutation}
                />
              ))}
            </div>
          )}
        </div>
      </Drawer>

      {/* Price History Modal */}
      {supplier && priceHistoryItem && isPriceHistoryOpen && (
        <PriceHistoryModal
          supplierId={supplier.id}
          item={priceHistoryItem}
          isOpen={isPriceHistoryOpen}
          onClose={handleClosePriceHistory}
        />
      )}
    </>
  );
}
